import React, { useState, useEffect } from 'react';
import audioThumbnailDefault from '@/assets/audio-thumbnail.png';

interface VideoThumbnailProps {
  src: string;
  alt?: string;
  className?: string;
  onClick?: () => void;
  isSelected?: boolean;
  isAudio?: boolean; // New prop to indicate if this is an audio file
}

/**
 * A component for progressively loading thumbnail images with a loading placeholder
 * and fallback for missing or broken images
 */
const VideoThumbnail: React.FC<VideoThumbnailProps> = ({ 
  src, 
  alt = 'Video thumbnail', 
  className = '',
  onClick,
  isSelected = false,
  isAudio = false
}) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [validSrc, setValidSrc] = useState(isAudio ? audioThumbnailDefault : src);
  
  // Reset state when src changes
  useEffect(() => {
    setLoaded(false);
    setError(false);
    
    // If it's an audio file, always use the audio thumbnail
    if (isAudio) {
      setValidSrc(audioThumbnailDefault);
      setLoaded(true);
      return;
    }
    
    // Basic URL validation to prevent unnecessary network requests
    if (!src) {
      console.warn('Empty thumbnail URL provided');
      setError(true);
      setLoaded(true);
      return;
    }
    
    // Check if URL has a valid format
    try {
      // Make sure src is a string before checking
      if (typeof src === 'string' && src.trim() !== '') {
        // Basic URL validation with standard URL constructor
        new URL(src);
        // URL looks valid - proceed with loading
        setValidSrc(src);
      } else {
        console.warn(`Invalid thumbnail URL format: ${src}`);
        setError(true);
        setLoaded(true);
      }
    } catch (e) {
      console.warn(`Malformed thumbnail URL: ${src}`, e);
      setError(true);
      setLoaded(true);
    }
  }, [src, isAudio]);
  
  const handleLoad = () => {
    console.log(`Successfully loaded thumbnail: ${src}`);
    setLoaded(true);
    setError(false);
  };
  
  const handleError = () => {
    console.warn(`Failed to load thumbnail image: ${src}`);
    setError(true);
    setLoaded(true); // Consider it "loaded" to remove loading state
  };
  
  return (
    <div 
      className={`relative overflow-hidden ${className} ${onClick ? 'cursor-pointer' : ''} ${isSelected ? 'ring-2 ring-[#FF4A8E]' : ''}`}
      onClick={onClick}
    >
      {/* Show placeholder until image is loaded */}
      {!loaded && !error && !isAudio && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse" />
      )}
      
      {/* Show fallback for error states */}
      {error && !isAudio ? (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <div className="flex flex-col items-center text-gray-400">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"></rect>
              <line x1="7" y1="2" x2="7" y2="22"></line>
              <line x1="17" y1="2" x2="17" y2="22"></line>
              <line x1="2" y1="12" x2="22" y2="12"></line>
              <line x1="2" y1="7" x2="7" y2="7"></line>
              <line x1="2" y1="17" x2="7" y2="17"></line>
              <line x1="17" y1="17" x2="22" y2="17"></line>
              <line x1="17" y1="7" x2="22" y2="7"></line>
            </svg>
            <span className="mt-2 text-sm">No preview available</span>
          </div>
        </div>
      ) : (
        <img
          src={validSrc}
          alt={alt}
          className={`transition-opacity duration-300 w-full h-full object-cover ${loaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={handleLoad}
          onError={handleError}
          loading="lazy"
          crossOrigin="anonymous" // Add cross-origin attribute to help with CORS issues
        />
      )}
      
      {/* Play button overlay - only show if we have a valid image and no errors */}
      {loaded && !error && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-12 h-12 bg-black bg-opacity-60 rounded-full flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
              <polygon points="5 3 19 12 5 21 5 3"></polygon>
            </svg>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoThumbnail; 